import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { Download, RefreshCw, FileText, Users, Calendar, DollarSign, BarChart3, PieChart, TrendingUp, Clock } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function ReportsAnalytics({ user, onLogout }) {
  const [activeReport, setActiveReport] = useState('dashboard');
  const [loading, setLoading] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [attendanceReport, setAttendanceReport] = useState(null);
  const [dailyAttendance, setDailyAttendance] = useState(null);
  const [leaveReport, setLeaveReport] = useState(null);
  const [payrollReport, setPayrollReport] = useState(null);
  const [employeeReport, setEmployeeReport] = useState(null);
  const [employees, setEmployees] = useState([]);
  
  const [attendanceFilters, setAttendanceFilters] = useState({
    start_date: new Date(new Date().setDate(1)).toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0],
    department: ''
  });

  const [dailyFilters, setDailyFilters] = useState({
    start_date: new Date(new Date().setDate(new Date().getDate() - 7)).toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0],
    employee_id: ''
  });

  const [leaveFilters, setLeaveFilters] = useState({
    year: new Date().getFullYear(),
    department: ''
  });

  const [payrollFilters, setPayrollFilters] = useState({
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    department: ''
  });

  useEffect(() => {
    fetchEmployees();
    if (activeReport === 'dashboard') {
      fetchDashboardAnalytics();
    } else if (activeReport === 'employees') {
      fetchEmployeeReport();
    }
  }, [activeReport]);

  const fetchEmployees = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/employees`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmployees(response.data || []);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const fetchDashboardAnalytics = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/reports/dashboard/analytics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboardData(response.data);
    } catch (error) {
      console.error('Error fetching dashboard analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployeeReport = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/employees`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmployeeReport(response.data || []);
    } catch (error) {
      console.error('Error fetching employees:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAttendanceReport = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams(attendanceFilters).toString();
      const response = await axios.get(`${API}/api/reports/attendance/summary?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAttendanceReport(response.data);
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const fetchDailyAttendance = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/attendance`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      let records = response.data || [];
      
      // Filter by date range
      records = records.filter(r => {
        const recordDate = r.date?.split('T')[0];
        return recordDate >= dailyFilters.start_date && recordDate <= dailyFilters.end_date;
      });
      
      // Filter by employee if selected
      if (dailyFilters.employee_id) {
        records = records.filter(r => r.employee_id === dailyFilters.employee_id);
      }
      
      // Sort by date descending, then by employee name
      records.sort((a, b) => {
        const dateCompare = new Date(b.date) - new Date(a.date);
        if (dateCompare !== 0) return dateCompare;
        return (a.employee_name || '').localeCompare(b.employee_name || '');
      });
      
      setDailyAttendance(records);
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const fetchLeaveReport = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams(leaveFilters).toString();
      const response = await axios.get(`${API}/api/reports/leaves/summary?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeaveReport(response.data);
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const fetchPayrollReport = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/api/payroll/month/${payrollFilters.month}/year/${payrollFilters.year}`,
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setPayrollReport(response.data);
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  // Download CSV Helper
  const downloadCSV = (data, headers, filename) => {
    const csvContent = [
      headers.map(h => h.label).join(','),
      ...data.map(row => headers.map(h => `"${row[h.key] || ''}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Download Excel (HTML Table Format)
  const downloadExcel = (tableId, filename) => {
    const table = document.getElementById(tableId);
    if (!table) return;
    
    const html = `
      <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel">
      <head><meta charset="UTF-8">
      <style>
        table { border-collapse: collapse; }
        th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
        th { background-color: #2b3380; color: white; }
      </style>
      </head>
      <body>${table.outerHTML}</body>
      </html>
    `;
    
    const blob = new Blob([html], { type: 'application/vnd.ms-excel' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}_${new Date().toISOString().split('T')[0]}.xls`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Download Daily Attendance Report
  const downloadDailyAttendance = () => {
    if (!dailyAttendance || dailyAttendance.length === 0) return;
    
    const data = dailyAttendance.map(r => ({
      date: r.date?.split('T')[0] || '',
      employee_name: r.employee_name || '',
      employee_id: r.employee_id || '',
      check_in: r.check_in ? new Date(r.check_in).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '-',
      check_out: r.check_out ? new Date(r.check_out).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '-',
      hours_worked: r.hours_worked ? r.hours_worked.toFixed(2) : '0',
      status: r.status || '',
      location: r.location || ''
    }));
    
    const headers = [
      { key: 'date', label: 'Date' },
      { key: 'employee_name', label: 'Employee Name' },
      { key: 'employee_id', label: 'Employee ID' },
      { key: 'check_in', label: 'Check In Time' },
      { key: 'check_out', label: 'Check Out Time' },
      { key: 'hours_worked', label: 'Hours Worked' },
      { key: 'status', label: 'Status' },
      { key: 'location', label: 'Location' }
    ];
    
    downloadCSV(data, headers, 'daily_attendance_report');
  };

  // Download Employee Report
  const downloadEmployeeReport = () => {
    if (!employeeReport) return;
    
    const data = employeeReport.map(emp => ({
      name: emp.name,
      id: emp.id,
      email: emp.email,
      mobile: emp.mobile_number,
      department: emp.departments?.join(', ') || '',
      designation: emp.designation,
      status: emp.status,
      joining_date: emp.date_of_joining
    }));
    
    const headers = [
      { key: 'name', label: 'Name' },
      { key: 'id', label: 'Employee ID' },
      { key: 'email', label: 'Email' },
      { key: 'mobile', label: 'Mobile' },
      { key: 'department', label: 'Department' },
      { key: 'designation', label: 'Designation' },
      { key: 'status', label: 'Status' },
      { key: 'joining_date', label: 'Joining Date' }
    ];
    
    downloadCSV(data, headers, 'employee_report');
  };

  // Download Payroll Report
  const downloadPayrollReport = () => {
    if (!payrollReport?.payroll_records) return;
    
    const data = payrollReport.payroll_records.map(r => ({
      employee_name: r.employee_name,
      employee_id: r.employee_id,
      gross_salary: r.gross_salary,
      total_deductions: r.total_deductions,
      net_salary: r.net_salary,
      basic: r.earnings?.basic || 0,
      hra: r.earnings?.hra || 0,
      pf: r.deductions?.pf_employee || 0
    }));
    
    const headers = [
      { key: 'employee_name', label: 'Employee Name' },
      { key: 'employee_id', label: 'Employee ID' },
      { key: 'gross_salary', label: 'Gross Salary' },
      { key: 'total_deductions', label: 'Deductions' },
      { key: 'net_salary', label: 'Net Salary' },
      { key: 'basic', label: 'Basic' },
      { key: 'hra', label: 'HRA' },
      { key: 'pf', label: 'PF' }
    ];
    
    downloadCSV(data, headers, 'payroll_report');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatTime = (dateTimeStr) => {
    if (!dateTimeStr) return '-';
    try {
      return new Date(dateTimeStr).toLocaleTimeString('en-IN', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: true 
      });
    } catch {
      return '-';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        weekday: 'short'
      });
    } catch {
      return dateStr;
    }
  };

  const getMonthName = (monthNum) => {
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    return months[monthNum - 1];
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'present': return 'bg-green-100 text-green-700';
      case 'absent': return 'bg-red-100 text-red-700';
      case 'half day': return 'bg-yellow-100 text-yellow-700';
      case 'late': return 'bg-orange-100 text-orange-700';
      case 'leave': return 'bg-blue-100 text-blue-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold" style={{color: '#2b3380'}}>📊 Reports & Analytics</h1>
            <p className="mt-2 text-gray-600">Comprehensive HR analytics and downloadable reports</p>
          </div>
        </div>

        {/* Report Type Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {[
            { id: 'dashboard', icon: <BarChart3 className="w-4 h-4" />, label: 'Dashboard' },
            { id: 'employees', icon: <Users className="w-4 h-4" />, label: 'Employee Report' },
            { id: 'daily-attendance', icon: <Clock className="w-4 h-4" />, label: 'Daily Attendance' },
            { id: 'attendance', icon: <Calendar className="w-4 h-4" />, label: 'Attendance Summary' },
            { id: 'leaves', icon: <FileText className="w-4 h-4" />, label: 'Leave Report' },
            { id: 'payroll', icon: <DollarSign className="w-4 h-4" />, label: 'Payroll Report' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveReport(tab.id)}
              className={`px-4 py-2.5 rounded-lg transition whitespace-nowrap flex items-center gap-2 text-sm ${
                activeReport === tab.id
                  ? 'text-white shadow-md'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              style={activeReport === tab.id ? {backgroundColor: '#2b3380'} : {}}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>

        {/* Dashboard View */}
        {activeReport === 'dashboard' && (
          <div>
            {loading ? (
              <div className="text-center py-12">
                <RefreshCw className="w-8 h-8 animate-spin mx-auto text-gray-400" />
                <p className="mt-3 text-gray-500">Loading analytics...</p>
              </div>
            ) : dashboardData ? (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg p-6 text-white shadow-lg">
                    <Users className="w-8 h-8 mb-2 opacity-80" />
                    <p className="text-sm opacity-90">Total Employees</p>
                    <p className="text-4xl font-bold mt-2">{dashboardData.total_employees || 0}</p>
                  </div>
                  <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg p-6 text-white shadow-lg">
                    <TrendingUp className="w-8 h-8 mb-2 opacity-80" />
                    <p className="text-sm opacity-90">Present Today</p>
                    <p className="text-4xl font-bold mt-2">{dashboardData.present_today || 0}</p>
                    <p className="text-xs mt-1">({dashboardData.attendance_rate_today || 0}%)</p>
                  </div>
                  <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-lg p-6 text-white shadow-lg">
                    <FileText className="w-8 h-8 mb-2 opacity-80" />
                    <p className="text-sm opacity-90">Pending Leaves</p>
                    <p className="text-4xl font-bold mt-2">{dashboardData.pending_leaves || 0}</p>
                  </div>
                  <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg p-6 text-white shadow-lg">
                    <DollarSign className="w-8 h-8 mb-2 opacity-80" />
                    <p className="text-sm opacity-90">Payroll (This Month)</p>
                    <p className="text-4xl font-bold mt-2">{dashboardData.payroll_generated_this_month || 0}</p>
                  </div>
                </div>

                {dashboardData.department_distribution && (
                  <div className="bg-white rounded-lg shadow-md p-6">
                    <h2 className="text-xl font-bold mb-4" style={{color: '#2b3380'}}>Department Distribution</h2>
                    <div className="space-y-3">
                      {Object.entries(dashboardData.department_distribution).slice(0, 10).map(([dept, count]) => (
                        <div key={dept} className="flex items-center">
                          <div className="w-40 text-sm font-medium text-gray-700 truncate">{dept}</div>
                          <div className="flex-1 bg-gray-200 rounded-full h-6 relative mx-4">
                            <div
                              className="h-6 rounded-full flex items-center justify-end pr-2"
                              style={{
                                width: `${Math.max((count / dashboardData.total_employees) * 100, 10)}%`,
                                backgroundColor: '#2b3380'
                              }}
                            >
                              <span className="text-xs text-white font-semibold">{count}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-12 bg-white rounded-lg shadow">
                <PieChart className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500">No analytics data available</p>
              </div>
            )}
          </div>
        )}

        {/* Employee Report */}
        {activeReport === 'employees' && (
          <div>
            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-xl font-bold" style={{color: '#2b3380'}}>Employee Master Report</h2>
                  <p className="text-gray-500 text-sm mt-1">Complete employee directory with details</p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={fetchEmployeeReport}
                    disabled={loading}
                    className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center gap-2"
                  >
                    <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                    Refresh
                  </button>
                  <button
                    onClick={downloadEmployeeReport}
                    disabled={!employeeReport || employeeReport.length === 0}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg flex items-center gap-2 hover:bg-green-700 disabled:bg-gray-300"
                  >
                    <Download className="w-4 h-4" />
                    Download CSV
                  </button>
                </div>
              </div>
            </div>

            {loading ? (
              <div className="text-center py-12 bg-white rounded-lg shadow">
                <RefreshCw className="w-8 h-8 animate-spin mx-auto text-gray-400" />
                <p className="mt-3 text-gray-500">Loading employees...</p>
              </div>
            ) : employeeReport && employeeReport.length > 0 ? (
              <div className="bg-white rounded-lg shadow-md overflow-hidden">
                <div className="px-6 py-3 border-b" style={{backgroundColor: '#f5f7fa'}}>
                  <p className="text-sm text-gray-600">Total: <strong>{employeeReport.length}</strong> employees</p>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead style={{backgroundColor: '#2b3380'}}>
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase">Name</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase">Employee ID</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase">Email</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase">Mobile</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase">Department</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {employeeReport.slice(0, 50).map((emp, idx) => (
                        <tr key={idx} className="hover:bg-gray-50">
                          <td className="px-4 py-3 font-medium">{emp.name}</td>
                          <td className="px-4 py-3 text-gray-600 text-sm">{emp.id}</td>
                          <td className="px-4 py-3 text-gray-600 text-sm">{emp.email}</td>
                          <td className="px-4 py-3 text-gray-600">{emp.mobile_number}</td>
                          <td className="px-4 py-3 text-gray-600 text-sm">{emp.departments?.join(', ') || '-'}</td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 rounded-full text-xs ${emp.status === 'Active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                              {emp.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : null}
          </div>
        )}

        {/* Daily Attendance Report - NEW */}
        {activeReport === 'daily-attendance' && (
          <div>
            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h2 className="text-xl font-bold" style={{color: '#2b3380'}}>📅 Daily Attendance Report</h2>
                  <p className="text-gray-500 text-sm">Day-wise attendance with check-in/check-out times</p>
                </div>
                {dailyAttendance && dailyAttendance.length > 0 && (
                  <button
                    onClick={downloadDailyAttendance}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg flex items-center gap-2 hover:bg-green-700"
                  >
                    <Download className="w-4 h-4" />
                    Download CSV
                  </button>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                  <input
                    type="date"
                    value={dailyFilters.start_date}
                    onChange={(e) => setDailyFilters({...dailyFilters, start_date: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                  <input
                    type="date"
                    value={dailyFilters.end_date}
                    onChange={(e) => setDailyFilters({...dailyFilters, end_date: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Employee (Optional)</label>
                  <select
                    value={dailyFilters.employee_id}
                    onChange={(e) => setDailyFilters({...dailyFilters, employee_id: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">All Employees</option>
                    {employees.map(emp => (
                      <option key={emp.id} value={emp.id}>{emp.name}</option>
                    ))}
                  </select>
                </div>
                <div className="flex items-end">
                  <button
                    onClick={fetchDailyAttendance}
                    disabled={loading}
                    className="w-full px-6 py-2 text-white rounded-lg flex items-center justify-center gap-2"
                    style={{backgroundColor: loading ? '#9e9e9e' : '#2b3380'}}
                  >
                    {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Clock className="w-4 h-4" />}
                    {loading ? 'Loading...' : 'Generate Report'}
                  </button>
                </div>
              </div>
            </div>

            {dailyAttendance && (
              <div>
                {/* Summary Stats */}
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
                  <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                    <p className="text-sm text-blue-600 font-medium">Total Records</p>
                    <p className="text-2xl font-bold text-blue-900">{dailyAttendance.length}</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                    <p className="text-sm text-green-600 font-medium">Present</p>
                    <p className="text-2xl font-bold text-green-900">
                      {dailyAttendance.filter(r => r.status?.toLowerCase() === 'present').length}
                    </p>
                  </div>
                  <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                    <p className="text-sm text-red-600 font-medium">Absent</p>
                    <p className="text-2xl font-bold text-red-900">
                      {dailyAttendance.filter(r => r.status?.toLowerCase() === 'absent').length}
                    </p>
                  </div>
                  <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                    <p className="text-sm text-yellow-600 font-medium">Half Day</p>
                    <p className="text-2xl font-bold text-yellow-900">
                      {dailyAttendance.filter(r => r.status?.toLowerCase() === 'half day').length}
                    </p>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                    <p className="text-sm text-purple-600 font-medium">Avg Hours</p>
                    <p className="text-2xl font-bold text-purple-900">
                      {dailyAttendance.length > 0 
                        ? (dailyAttendance.reduce((sum, r) => sum + (r.hours_worked || 0), 0) / dailyAttendance.length).toFixed(1)
                        : '0'}
                    </p>
                  </div>
                </div>

                {dailyAttendance.length > 0 ? (
                  <div className="bg-white rounded-lg shadow-md overflow-hidden">
                    <div className="px-6 py-3 border-b" style={{backgroundColor: '#f5f7fa'}}>
                      <p className="text-sm text-gray-600">
                        Showing <strong>{Math.min(dailyAttendance.length, 100)}</strong> of <strong>{dailyAttendance.length}</strong> records
                      </p>
                    </div>
                    <div className="overflow-x-auto">
                      <table id="daily-attendance-table" className="min-w-full">
                        <thead style={{backgroundColor: '#2b3380'}}>
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase">Date</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase">Employee</th>
                            <th className="px-4 py-3 text-center text-xs font-medium text-white uppercase">Check In</th>
                            <th className="px-4 py-3 text-center text-xs font-medium text-white uppercase">Check Out</th>
                            <th className="px-4 py-3 text-center text-xs font-medium text-white uppercase">Hours Worked</th>
                            <th className="px-4 py-3 text-center text-xs font-medium text-white uppercase">Status</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase">Location</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {dailyAttendance.slice(0, 100).map((record, idx) => (
                            <tr key={idx} className="hover:bg-gray-50">
                              <td className="px-4 py-3 font-medium text-sm">{formatDate(record.date)}</td>
                              <td className="px-4 py-3">
                                <div className="font-medium">{record.employee_name}</div>
                                <div className="text-xs text-gray-500">{record.employee_id}</div>
                              </td>
                              <td className="px-4 py-3 text-center">
                                <span className="font-mono text-green-700 font-semibold">
                                  {formatTime(record.check_in)}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-center">
                                <span className="font-mono text-red-700 font-semibold">
                                  {formatTime(record.check_out)}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-center">
                                <span className={`font-bold ${record.hours_worked >= 8 ? 'text-green-600' : record.hours_worked >= 4 ? 'text-yellow-600' : 'text-red-600'}`}>
                                  {record.hours_worked ? record.hours_worked.toFixed(1) : '0'} hrs
                                </span>
                              </td>
                              <td className="px-4 py-3 text-center">
                                <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(record.status)}`}>
                                  {record.status}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">{record.location || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {dailyAttendance.length > 100 && (
                      <div className="px-6 py-3 border-t bg-gray-50 text-center text-sm text-gray-500">
                        Download CSV for complete data ({dailyAttendance.length} records)
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-12 bg-white rounded-lg shadow">
                    <Clock className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                    <p className="text-gray-500">No attendance records found for the selected date range</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Attendance Summary Report */}
        {activeReport === 'attendance' && (
          <div>
            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h2 className="text-xl font-bold" style={{color: '#2b3380'}}>Attendance Summary Report</h2>
                  <p className="text-gray-500 text-sm">Employee-wise attendance summary for date range</p>
                </div>
                {attendanceReport && (
                  <button
                    onClick={() => downloadExcel('attendance-report-table', 'attendance_summary')}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg flex items-center gap-2 hover:bg-green-700"
                  >
                    <Download className="w-4 h-4" />
                    Download Excel
                  </button>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                  <input
                    type="date"
                    value={attendanceFilters.start_date}
                    onChange={(e) => setAttendanceFilters({...attendanceFilters, start_date: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                  <input
                    type="date"
                    value={attendanceFilters.end_date}
                    onChange={(e) => setAttendanceFilters({...attendanceFilters, end_date: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
                  <input
                    type="text"
                    value={attendanceFilters.department}
                    onChange={(e) => setAttendanceFilters({...attendanceFilters, department: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="All Departments"
                  />
                </div>
                <div className="flex items-end">
                  <button
                    onClick={fetchAttendanceReport}
                    disabled={loading}
                    className="w-full px-6 py-2 text-white rounded-lg flex items-center justify-center gap-2"
                    style={{backgroundColor: loading ? '#9e9e9e' : '#2b3380'}}
                  >
                    {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <BarChart3 className="w-4 h-4" />}
                    {loading ? 'Generating...' : 'Generate Report'}
                  </button>
                </div>
              </div>
            </div>

            {attendanceReport && (
              <div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                  <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                    <p className="text-sm text-blue-600 font-medium">Total Employees</p>
                    <p className="text-2xl font-bold text-blue-900">{attendanceReport.summary?.total_employees || 0}</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                    <p className="text-sm text-green-600 font-medium">Total Present</p>
                    <p className="text-2xl font-bold text-green-900">{attendanceReport.summary?.total_present || 0}</p>
                  </div>
                  <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                    <p className="text-sm text-red-600 font-medium">Total Absent</p>
                    <p className="text-2xl font-bold text-red-900">{attendanceReport.summary?.total_absent || 0}</p>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                    <p className="text-sm text-purple-600 font-medium">Avg Attendance Rate</p>
                    <p className="text-2xl font-bold text-purple-900">{attendanceReport.summary?.average_attendance_rate || 0}%</p>
                  </div>
                </div>

                {attendanceReport.employee_stats && (
                  <div className="bg-white rounded-lg shadow-md overflow-hidden">
                    <div className="overflow-x-auto">
                      <table id="attendance-report-table" className="min-w-full">
                        <thead style={{backgroundColor: '#2b3380'}}>
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase">Employee</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase">Department</th>
                            <th className="px-4 py-3 text-center text-xs font-medium text-white uppercase">Present</th>
                            <th className="px-4 py-3 text-center text-xs font-medium text-white uppercase">Absent</th>
                            <th className="px-4 py-3 text-center text-xs font-medium text-white uppercase">Rate</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {attendanceReport.employee_stats.map((emp, idx) => (
                            <tr key={idx} className="hover:bg-gray-50">
                              <td className="px-4 py-3">
                                <div className="font-medium">{emp.employee_name}</div>
                                <div className="text-xs text-gray-500">{emp.employee_id}</div>
                              </td>
                              <td className="px-4 py-3 text-gray-600 text-sm">{emp.departments?.join(', ') || '-'}</td>
                              <td className="px-4 py-3 text-center font-semibold text-green-600">{emp.total_present}</td>
                              <td className="px-4 py-3 text-center font-semibold text-red-600">{emp.total_absent}</td>
                              <td className="px-4 py-3 text-center">
                                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                                  emp.attendance_rate >= 90 ? 'bg-green-100 text-green-800' :
                                  emp.attendance_rate >= 75 ? 'bg-yellow-100 text-yellow-800' :
                                  'bg-red-100 text-red-800'
                                }`}>
                                  {emp.attendance_rate}%
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Leave Report */}
        {activeReport === 'leaves' && (
          <div>
            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h2 className="text-xl font-bold" style={{color: '#2b3380'}}>Leave Report</h2>
                  <p className="text-gray-500 text-sm">Generate leave summary for year</p>
                </div>
                {leaveReport && (
                  <button
                    onClick={() => downloadExcel('leave-report-table', 'leave_report')}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg flex items-center gap-2 hover:bg-green-700"
                  >
                    <Download className="w-4 h-4" />
                    Download Excel
                  </button>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
                  <select
                    value={leaveFilters.year}
                    onChange={(e) => setLeaveFilters({...leaveFilters, year: parseInt(e.target.value)})}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    {[2024, 2025, 2026].map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
                  <input
                    type="text"
                    value={leaveFilters.department}
                    onChange={(e) => setLeaveFilters({...leaveFilters, department: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="All Departments"
                  />
                </div>
                <div className="flex items-end">
                  <button
                    onClick={fetchLeaveReport}
                    disabled={loading}
                    className="w-full px-6 py-2 text-white rounded-lg flex items-center justify-center gap-2"
                    style={{backgroundColor: loading ? '#9e9e9e' : '#2b3380'}}
                  >
                    {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                    {loading ? 'Generating...' : 'Generate Report'}
                  </button>
                </div>
              </div>
            </div>

            {leaveReport && (
              <div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                  <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                    <p className="text-sm text-blue-600 font-medium">Total Requests</p>
                    <p className="text-2xl font-bold text-blue-900">{leaveReport.summary?.total_requests || 0}</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                    <p className="text-sm text-green-600 font-medium">Approved</p>
                    <p className="text-2xl font-bold text-green-900">{leaveReport.summary?.approved || 0}</p>
                  </div>
                  <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                    <p className="text-sm text-yellow-600 font-medium">Pending</p>
                    <p className="text-2xl font-bold text-yellow-900">{leaveReport.summary?.pending || 0}</p>
                  </div>
                  <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                    <p className="text-sm text-red-600 font-medium">Rejected</p>
                    <p className="text-2xl font-bold text-red-900">{leaveReport.summary?.rejected || 0}</p>
                  </div>
                </div>

                {leaveReport.employee_stats && (
                  <div className="bg-white rounded-lg shadow-md overflow-hidden">
                    <div className="overflow-x-auto">
                      <table id="leave-report-table" className="min-w-full">
                        <thead style={{backgroundColor: '#2b3380'}}>
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase">Employee</th>
                            <th className="px-4 py-3 text-center text-xs font-medium text-white uppercase">Total</th>
                            <th className="px-4 py-3 text-center text-xs font-medium text-white uppercase">Approved</th>
                            <th className="px-4 py-3 text-center text-xs font-medium text-white uppercase">Pending</th>
                            <th className="px-4 py-3 text-center text-xs font-medium text-white uppercase">Rejected</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {leaveReport.employee_stats.map((emp, idx) => (
                            <tr key={idx} className="hover:bg-gray-50">
                              <td className="px-4 py-3 font-medium">{emp.employee_name}</td>
                              <td className="px-4 py-3 text-center">{emp.total}</td>
                              <td className="px-4 py-3 text-center text-green-600 font-semibold">{emp.approved}</td>
                              <td className="px-4 py-3 text-center text-yellow-600 font-semibold">{emp.pending}</td>
                              <td className="px-4 py-3 text-center text-red-600 font-semibold">{emp.rejected}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Payroll Report */}
        {activeReport === 'payroll' && (
          <div>
            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h2 className="text-xl font-bold" style={{color: '#2b3380'}}>Payroll Report</h2>
                  <p className="text-gray-500 text-sm">Generate monthly payroll summary</p>
                </div>
                {payrollReport?.payroll_records?.length > 0 && (
                  <button
                    onClick={downloadPayrollReport}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg flex items-center gap-2 hover:bg-green-700"
                  >
                    <Download className="w-4 h-4" />
                    Download CSV
                  </button>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Month</label>
                  <select
                    value={payrollFilters.month}
                    onChange={(e) => setPayrollFilters({...payrollFilters, month: parseInt(e.target.value)})}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => (
                      <option key={m} value={m}>{getMonthName(m)}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
                  <select
                    value={payrollFilters.year}
                    onChange={(e) => setPayrollFilters({...payrollFilters, year: parseInt(e.target.value)})}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    {[2024, 2025, 2026].map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>
                <div className="flex items-end">
                  <button
                    onClick={fetchPayrollReport}
                    disabled={loading}
                    className="w-full px-6 py-2 text-white rounded-lg flex items-center justify-center gap-2"
                    style={{backgroundColor: loading ? '#9e9e9e' : '#2b3380'}}
                  >
                    {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <DollarSign className="w-4 h-4" />}
                    {loading ? 'Generating...' : 'Generate Report'}
                  </button>
                </div>
              </div>
            </div>

            {payrollReport && (
              <div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                  <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                    <p className="text-sm text-blue-600 font-medium">Total Payslips</p>
                    <p className="text-2xl font-bold text-blue-900">{payrollReport.payroll_records?.length || 0}</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                    <p className="text-sm text-green-600 font-medium">Total Gross</p>
                    <p className="text-xl font-bold text-green-900">
                      {formatCurrency(payrollReport.payroll_records?.reduce((sum, p) => sum + (p.gross_salary || 0), 0))}
                    </p>
                  </div>
                  <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                    <p className="text-sm text-red-600 font-medium">Total Deductions</p>
                    <p className="text-xl font-bold text-red-900">
                      {formatCurrency(payrollReport.payroll_records?.reduce((sum, p) => sum + (p.total_deductions || 0), 0))}
                    </p>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                    <p className="text-sm text-purple-600 font-medium">Total Net</p>
                    <p className="text-xl font-bold text-purple-900">
                      {formatCurrency(payrollReport.payroll_records?.reduce((sum, p) => sum + (p.net_salary || 0), 0))}
                    </p>
                  </div>
                </div>

                {payrollReport.payroll_records?.length > 0 && (
                  <div className="bg-white rounded-lg shadow-md overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="min-w-full">
                        <thead style={{backgroundColor: '#2b3380'}}>
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase">Employee</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase">ID</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-white uppercase">Gross</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-white uppercase">Deductions</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-white uppercase">Net Salary</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {payrollReport.payroll_records.map((record, idx) => (
                            <tr key={idx} className="hover:bg-gray-50">
                              <td className="px-4 py-3 font-medium">{record.employee_name}</td>
                              <td className="px-4 py-3 text-gray-600 text-sm">{record.employee_id}</td>
                              <td className="px-4 py-3 text-right font-mono">{formatCurrency(record.gross_salary)}</td>
                              <td className="px-4 py-3 text-right font-mono text-red-600">{formatCurrency(record.total_deductions)}</td>
                              <td className="px-4 py-3 text-right font-mono font-bold text-green-600">{formatCurrency(record.net_salary)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}

export default ReportsAnalytics;
